import React, { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip
} from '@mui/material';
import {
    MonetizationOn,
    DirectionsCar,
    Build,
    LocalGasStation
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { fuelApi, FuelTransaction } from '../services/fuelApi';
import { mileageApi, MileageLog } from '../services/mileageApi';
import { maintenanceApi } from '../services/maintenanceApi';
import { repairsApi } from '../services/repairsApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

interface VehicleTco {
    vehicleId: number;
    registration: string;
    brand: string;
    model: string;
    mileage: number;
    fuelCost: number;
    maintenanceCost: number;
    repairsCost: number;
    totalCost: number;
    costPerKm: number;
}

const TcoAnalysis: React.FC = () => {
    const { t } = useLanguage();
    const [tcoData, setTcoData] = useState<VehicleTco[]>([]);

    const calculateTco = async () => {
        try {
            const [vehRes, fuelRes, mileageRes, maintRes, repairList] = await Promise.all([
                vehiclesApi.getAll(),
                fuelApi.getAll(),
                mileageApi.getAll(),
                maintenanceApi.getAll(),
                repairsApi.getRepairs().catch(err => [])
            ]);

            if (vehRes.success && fuelRes.success && mileageRes.success && maintRes.success) {
                const vehiclesList: Vehicle[] = vehRes.data;
                const fuelList: FuelTransaction[] = fuelRes.data;
                const mileageList: MileageLog[] = mileageRes.data;
                const maintList: any[] = maintRes.data;

                const tcoCalculated: VehicleTco[] = vehiclesList.map(veh => {
                    // Filter costs for this specific vehicle
                    const vehicleFuelCost = fuelList
                        .filter(f => f.vehicleId === veh.id)
                        .reduce((sum, f) => sum + (f.cost || 0), 0);
                        
                    const vehicleMaintCost = maintList
                        .filter(m => m.vehicleId === veh.id)
                        .reduce((sum, m) => sum + (m.cost || 0), 0);

                    const vehicleRepairCost = repairList
                        .filter(r => r.vehIdf === veh.id)
                        .reduce((sum, r) => sum + (r.rprCost || 0), 0);

                    // Get total mileage distance registered in logs for this vehicle
                    const vehicleKms = mileageList
                        .filter(m => m.vehicleId === veh.id)
                        .reduce((sum, m) => sum + (m.distance || 0), 0);

                    const totalCost = vehicleFuelCost + vehicleMaintCost + vehicleRepairCost;
                    const finalDistance = vehicleKms > 0 ? vehicleKms : (veh.mileage || 1);
                    const costPerKm = totalCost / finalDistance;

                    return {
                        vehicleId: veh.id,
                        registration: veh.registrationNumber,
                        brand: veh.brand,
                        model: veh.model,
                        mileage: finalDistance,
                        fuelCost: vehicleFuelCost,
                        maintenanceCost: vehicleMaintCost,
                        repairsCost: vehicleRepairCost,
                        totalCost,
                        costPerKm
                    };
                });

                // Sort by total cost descending
                setTcoData(tcoCalculated.sort((a, b) => b.totalCost - a.totalCost));
            }
        } catch (error) {
            console.error('Failed to calculate TCO:', error);
        }
    };

    useEffect(() => {
        calculateTco();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Global aggregations
    const grandTotalTco = tcoData.reduce((sum, item) => sum + item.totalCost, 0);
    const totalFuelAgg = tcoData.reduce((sum, item) => sum + item.fuelCost, 0);
    const totalMaintAgg = tcoData.reduce((sum, item) => sum + item.maintenanceCost, 0);
    const totalRepairAgg = tcoData.reduce((sum, item) => sum + item.repairsCost, 0);

    return (
        <GenericPage title={t('parc.analysis.tcoTitle') || "Analyse TCO (Coût Total de Possession)"} footerEnabled={false}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Global TCO Aggregates */}
                <Grid item xs={12} sm={3}>
                    <Card sx={{ borderLeft: '6px solid #1e3c72' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <MonetizationOn color="primary" />
                                <Typography variant="subtitle2" color="text.secondary">{t('parc.analysis.globalTco')}</Typography>
                            </Box>
                            <Typography variant="h5">{grandTotalTco.toLocaleString('fr-FR')} DZD</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card sx={{ borderLeft: '6px solid #f5af19' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocalGasStation sx={{ color: '#f5af19' }} />
                                <Typography variant="subtitle2" color="text.secondary">{t('parc.analysis.totalFuel')}</Typography>
                            </Box>
                            <Typography variant="h5">{totalFuelAgg.toLocaleString('fr-FR')} DZD</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card sx={{ borderLeft: '6px solid #11998e' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Build sx={{ color: '#11998e' }} />
                                <Typography variant="subtitle2" color="text.secondary">{t('parc.analysis.totalMaint')}</Typography>
                            </Box>
                            <Typography variant="h5">{totalMaintAgg.toLocaleString('fr-FR')} DZD</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card sx={{ borderLeft: '6px solid #f12711' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DirectionsCar sx={{ color: '#f12711' }} />
                                <Typography variant="subtitle2" color="text.secondary">{t('parc.analysis.totalRepairs')}</Typography>
                            </Box>
                            <Typography variant="h5">{totalRepairAgg.toLocaleString('fr-FR')} DZD</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Detailed Vehicle Cost Table */}
            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead sx={{ backgroundColor: 'action.hover' }}>
                        <TableRow>
                            <TableCell><b>{t('parc.gpsTracking.vehicle')}</b></TableCell>
                            <TableCell align="right"><b>{t('menu.parc.fuel') || 'Carburant'} (DZD)</b></TableCell>
                            <TableCell align="right"><b>{t('parc.maintenance.title') || 'Maintenance'} (DZD)</b></TableCell>
                            <TableCell align="right"><b>{t('parc.repairs.title') || 'Réparations'} (DZD)</b></TableCell>
                            <TableCell align="right"><b>{t('parc.analysis.globalTco') || 'TCO Total'}</b></TableCell>
                            <TableCell align="right"><b>{t('parc.mileage.distance') || 'Distance'} (km)</b></TableCell>
                            <TableCell align="right"><b>{t('parc.analysis.costPerKm')}</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tcoData.map((row) => (
                            <TableRow key={row.vehicleId} hover>
                                <TableCell>
                                    <Typography variant="subtitle2">{row.registration}</Typography>
                                    <Typography variant="caption" color="text.secondary">{row.brand} {row.model}</Typography>
                                </TableCell>
                                <TableCell align="right">{row.fuelCost.toLocaleString('fr-FR')}</TableCell>
                                <TableCell align="right">{row.maintenanceCost.toLocaleString('fr-FR')}</TableCell>
                                <TableCell align="right">{row.repairsCost.toLocaleString('fr-FR')}</TableCell>
                                <TableCell align="right"><b>{row.totalCost.toLocaleString('fr-FR')} DZD</b></TableCell>
                                <TableCell align="right">{row.mileage.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    <Chip 
                                        label={`${row.costPerKm.toFixed(2)} DZD/km`} 
                                        color={row.costPerKm > 50 ? 'error' : row.costPerKm > 20 ? 'warning' : 'success'} 
                                        size="small" 
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {tcoData.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">{t('common.status.noData')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </GenericPage>
    );
};

export default TcoAnalysis;
